/**
 * Badge — labelled pill that sits INLINE with text.
 *
 * Sizing mirrors Figma — Tiny / Small / Normal / Big → xs / s / m / l.
 * Pill shape: `borderRadius = height / 2` at every size.
 *
 * Variants:
 *   - `solid`   = Figma "Bright": bg = role.main, fg = contrastText.
 *   - `soft`    = Figma "Gentle": bg = role.container, fg = onContainer.
 *   - `outline` (web extension): transparent bg, 1px border in role.main.
 *
 * Tones: primary / good / bad / alert / neutral. Neutral solid uses
 * inverse-surface (bright pill on dark page) per Figma; neutral soft
 * uses a translucent overlay.
 *
 * Semantic rule: content color = on-{tone} for the chosen fill.
 *
 * @format
 */

import {forwardRef, isValidElement, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text, iconPxForRole, type TypographyRole} from 'ui/theme/typography';

import {Icon} from '../Icon';

export const BADGE_SIZES = ['tiny', 'xs', 's', 'm'] as const;
export type BadgeSize = (typeof BADGE_SIZES)[number];

export const BADGE_TONES = ['primary', 'good', 'bad', 'alert', 'neutral'] as const;
export type BadgeTone = (typeof BADGE_TONES)[number];

export const BADGE_VARIANTS = ['solid', 'soft', 'outline'] as const;
export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

interface SizeGeometry {
  height: number;
  padX: number;
  padY: number;
  gap: number;
  iconPx: number;
  labelRole: TypographyRole;
}

const GEO: Record<BadgeSize, SizeGeometry> = {
  tiny: {height: 18, padX: 4, padY: 2, gap: 2, iconPx: 14, labelRole: 'label.s'},
  xs: {height: 24, padX: 8, padY: 4, gap: 4, iconPx: 16, labelRole: 'label.m'},
  s: {height: 32, padX: 16, padY: 8, gap: 4, iconPx: 16, labelRole: 'label.m'},
  m: {height: 48, padX: 16, padY: 14, gap: 4, iconPx: 20, labelRole: 'label.l'},
};

interface VariantPalette {
  bg: string;
  fg: string;
  border: string;
}

function paletteFor(
  theme: import('@mui/material/styles').Theme,
  variant: BadgeVariant,
  tone: BadgeTone,
): VariantPalette {
  const p = theme.palette;

  if (tone === 'neutral') {
    if (variant === 'solid') {
      return {
        bg: p.surface.inverseSurface,
        fg: p.surface.inverseOnSurface,
        border: 'transparent',
      };
    }
    if (variant === 'soft') {
      return {
        bg: 'rgba(250, 251, 254, 0.1)',
        fg: p.surface.onSurface,
        border: 'transparent',
      };
    }
    return {
      bg: 'transparent',
      fg: p.surface.onSurface,
      border: p.surface.onSurfaceMuted,
    };
  }

  const role = p[tone];
  if (variant === 'solid') {
    return {bg: role.main, fg: role.contrastText, border: 'transparent'};
  }
  if (variant === 'soft') {
    return {bg: role.container, fg: role.onContainer, border: 'transparent'};
  }
  return {bg: 'transparent', fg: role.main, border: role.main};
}

// ── Styled primitives ──────────────────────────────────────────────────

const Pill = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: BadgeSize;
  $variant: BadgeVariant;
  $tone: BadgeTone;
}>(({theme, $size, $variant, $tone}) => {
  const g = GEO[$size];
  const p = paletteFor(theme, $variant, $tone);
  return {
    display: 'inline-flex',
    alignItems: 'stretch',
    minHeight: g.height,
    borderRadius: g.height / 2,
    boxSizing: 'border-box',
    flexShrink: 0,
    backgroundColor: p.bg,
    border: $variant === 'outline' ? `1px solid ${p.border}` : 'none',
  };
});

const LabelBlock = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: BadgeSize;
  $variant: BadgeVariant;
  $tone: BadgeTone;
}>(({theme, $size, $variant, $tone}) => {
  const g = GEO[$size];
  const p = paletteFor(theme, $variant, $tone);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: g.gap,
    padding: `${g.padY}px ${g.padX}px`,
    color: p.fg,
    whiteSpace: 'nowrap',
    ...text(g.labelRole),
    lineHeight: 1,
  };
});

export interface BadgeProps {
  leftIcon?: string | ReactNode;
  children?: ReactNode;
  size?: BadgeSize;
  tone?: BadgeTone;
  variant?: BadgeVariant;
  className?: string;
  style?: React.CSSProperties;
}

function renderIcon(icon: string | ReactNode | undefined, px: number): ReactNode {
  if (!icon) return null;
  if (typeof icon === 'string') return <Icon name={icon} size={px} color='inherit' />;
  if (isValidElement(icon)) return icon;
  return icon;
}

export const Badge = forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  {leftIcon, children, size = 's', tone = 'primary', variant = 'solid', className, style},
  ref,
) {
  const iconPx = iconPxForRole(GEO[size].labelRole);
  return (
    <Pill
      ref={ref}
      $size={size}
      $variant={variant}
      $tone={tone}
      className={className}
      style={style}
    >
      <LabelBlock $size={size} $variant={variant} $tone={tone}>
        {renderIcon(leftIcon, iconPx)}
        {children != null && <Box component='span'>{children}</Box>}
      </LabelBlock>
    </Pill>
  );
});
